import { Table, Thead, Tbody, Tr, Th, Td, TableContainer, Text, IconButton, Badge, HStack, Tooltip } from "@chakra-ui/react";
import { FaDownload, FaEdit, FaFileUpload, FaTrash } from "react-icons/fa";
import { memo, useMemo } from "react";
import {
    PRICE_LIST_FORMAT_LABELS,
    PRICE_LIST_STATUS,
    PRICE_LIST_STATUS_BADGES,
    formatDateOnly,
    formatFileSize,
    getPriceListStatuses,
} from "../../constants/priceLists";
import { stickyColHeaderProps, stickyColCellProps } from "../../constants/tableStyles";

const PriceListTable = ({ priceLists, onDownload, onEdit, onDelete, downloadingId }) => {
    const statuses = useMemo(() => getPriceListStatuses(priceLists), [priceLists]);

    if (priceLists.length === 0) {
        return <Text align="center" color="gray.500">Nema cjenika. Učitajte prvi cjenik.</Text>;
    }

    return (
        <TableContainer overflowX="auto">
            <Table variant="striped" colorScheme="gray" size="sm">
                <Thead>
                    <Tr>
                        <Th {...stickyColHeaderProps}>#</Th>
                        <Th>Vrijedi od</Th>
                        <Th>Javni naziv</Th>
                        <Th>Status</Th>
                        <Th>Format</Th>
                        <Th isNumeric>Veličina</Th>
                        <Th>Akcije</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {priceLists.map((priceList, index) => {
                        const status = statuses[priceList.id];
                        const badge = PRICE_LIST_STATUS_BADGES[status];
                        const isScheduled = status === PRICE_LIST_STATUS.SCHEDULED;
                        const editLabel = isScheduled ? "Uredi cjenik" : "Objavi novu verziju";

                        return (
                            <Tr key={priceList.id} _hover={{ bg: "brand.50" }}>
                                <Td {...stickyColCellProps(index % 2 === 0)}>{index + 1}</Td>
                                <Td fontWeight="bold">{formatDateOnly(priceList.validForDate)}</Td>
                                <Td>
                                    <Text>{priceList.fileName}</Text>
                                    {priceList.originalFileName && (
                                        <Text fontSize="xs" color="gray.500">Učitano: {priceList.originalFileName}</Text>
                                    )}
                                </Td>
                                <Td>
                                    <HStack spacing={1}>
                                        <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                                        {priceList.isAutoGenerated && <Badge colorScheme="purple">Automatski</Badge>}
                                    </HStack>
                                </Td>
                                <Td>{PRICE_LIST_FORMAT_LABELS[priceList.format] ?? priceList.format}</Td>
                                <Td isNumeric>{formatFileSize(priceList.sizeInBytes)}</Td>
                                <Td>
                                    <Tooltip label="Preuzmi">
                                        <IconButton
                                            icon={<FaDownload />}
                                            size="sm"
                                            mr={2}
                                            aria-label="Preuzmi cjenik"
                                            isLoading={downloadingId === priceList.id}
                                            onClick={() => onDownload(priceList)}
                                        />
                                    </Tooltip>
                                    <Tooltip label={editLabel}>
                                        <IconButton
                                            icon={isScheduled ? <FaEdit /> : <FaFileUpload />}
                                            size="sm"
                                            mr={2}
                                            aria-label={editLabel}
                                            onClick={() => onEdit(priceList)}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Obriši">
                                        <IconButton
                                            icon={<FaTrash />}
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            aria-label="Obriši cjenik"
                                            onClick={() => onDelete(priceList)}
                                        />
                                    </Tooltip>
                                </Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </TableContainer>
    );
};

export default memo(PriceListTable);
